/**
 * authorizationConfig.js
 *
 * Per-page group restriction configuration.
 * Edit only the env files to change access — no code changes needed.
 *
 * ── Env variable formats ──────────────────────────────────────────────────
 *
 *   GUID list (legacy):  REACT_APP_<PAGE>_GROUP_LIST=guid1,guid2
 *   Name list:           REACT_APP_<PAGE>_GROUP_NAME=GroupA,Group With Space,GroupB
 *   Allow all:           REACT_APP_<PAGE>_GROUP_NAME=*
 *
 *   • Names are matched case-insensitively against the token `groups` claim.
 *   • Spaces inside group names are supported — only commas are separators.
 *   • Setting the value to exactly * skips group restriction — every authenticated
 *     user is granted access.  * is not treated as a group name.
 *   • Leave a variable unset / blank to deny access to everyone (safe default).
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parse a comma-separated list of Azure AD Object GUIDs from an env variable.
 * Returns an array of trimmed, non-empty strings.
 * Returns an empty array when the variable is unset or blank.
 *
 * @param {string} envVarName  e.g. 'REACT_APP_POINT_ADJUSTMENT_GROUP_LIST'
 * @returns {string[]}
 */
function parseGroupList(envVarName) {
  return (process.env[envVarName] || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Parse a comma-separated list of group display names from an env variable.
 * Names are lowercased for case-insensitive matching at runtime.
 * Group names may contain spaces — only commas are used as separators.
 *
 * Special value: if the entire variable is exactly "*" (just an asterisk),
 * the returned array will be ['*'] and useAuthorization will skip group
 * restriction entirely — every authenticated user is granted access.
 * * is not treated as a group name and will never match a token group.
 *
 * Returns an empty array when the variable is unset or blank → deny all.
 *
 * @param {string} envVarName  e.g. 'REACT_APP_POINT_ADJUSTMENT_GROUP_NAME'
 * @returns {string[]}  lowercase display names (may contain '*' as sentinel)
 */
function parseGroupNameList(envVarName) {
  const raw = (process.env[envVarName] || '').trim();
  if (!raw) return [];
  // Single asterisk → sentinel meaning "allow all authenticated users"
  if (raw === '*') return ['*'];
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

// ─── Page group lists ─────────────────────────────────────────────────────────

/**
 * GUIDs allowed to access the Point Adjustments page.
 * Source: REACT_APP_POINT_ADJUSTMENT_GROUP_LIST (comma-separated GUIDs)
 * Leave blank / commented out when using name-based matching instead.
 */
export const POINT_ADJUSTMENT_ALLOWED_GROUPS = parseGroupList('REACT_APP_POINT_ADJUSTMENT_GROUP_LIST');

/**
 * Display names (sAMAccountName) allowed to access the Point Adjustments page.
 * Source: REACT_APP_POINT_ADJUSTMENT_GROUP_NAME (comma-separated names)
 * Used when token contains group names instead of GUIDs.
 */
export const POINT_ADJUSTMENT_ALLOWED_GROUP_NAMES = parseGroupNameList('REACT_APP_POINT_ADJUSTMENT_GROUP_NAME');

/**
 * GUIDs allowed to access the Earn Points page.
 * Source: REACT_APP_EARN_POINTS_GROUP_LIST (comma-separated GUIDs)
 */
export const EARN_POINTS_ALLOWED_GROUPS = parseGroupList('REACT_APP_EARN_POINTS_GROUP_LIST');

/**
 * Display names (sAMAccountName) allowed to access the Earn Points page.
 * Source: REACT_APP_EARN_POINTS_GROUP_NAME (comma-separated names)
 */
export const EARN_POINTS_ALLOWED_GROUP_NAMES = parseGroupNameList('REACT_APP_EARN_POINTS_GROUP_NAME');

// ─── Adding a new page ────────────────────────────────────────────────────────
// 1. Add env vars:  REACT_APP_<PAGE>_GROUP_LIST=guid1,guid2   (GUID match)
//                   REACT_APP_<PAGE>_GROUP_NAME=Name1,Name2   (name match)
// 2. Export lists:  export const <PAGE>_ALLOWED_GROUPS      = parseGroupList('REACT_APP_<PAGE>_GROUP_LIST');
//                   export const <PAGE>_ALLOWED_GROUP_NAMES = parseGroupNameList('REACT_APP_<PAGE>_GROUP_NAME');
// 3. Use it:        const { canAccess<Page> } = useAuthorization();
